// src/pages/AdminLessonsPage.tsx
import { useEffect, useState } from 'react';
import authAxios from '../auth/authAxios';

interface Lesson {
    lesson_id: string;
    title: string;
    description: string;
    completed: boolean;
    objective?: string;
    steps?: string;
    example_prompt?: string;
    hint?: string;
    expected_outcome?: string;
  }

interface NewLesson {
    title: string;
    description: string;
    objective?: string;
    steps?: string;
    example_prompt?: string;
    hint?: string;
    expected_outcome?: string;
}

export default function AdminLessonsPage() {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [newLesson, setNewLesson] = useState<NewLesson>({
    title: '',
    description: '',
    objective: '',
    steps: '',
    example_prompt: '',
    hint: '',
    expected_outcome: '',
  });
  const [editLesson, setEditLesson] = useState<Lesson | null>(null);

  const fetchLessons = async () => {
    const res = await authAxios.get('/api/lessons/admin/all');
    setLessons(res.data);
  };

  useEffect(() => {
    fetchLessons();
  }, []);

  const handleCreate = async () => {
    try {
      await authAxios.post('/api/lessons/admin/create', newLesson);
      // reload lessons
      fetchLessons(); 
      // clear form
      setNewLesson({
        title: '',
        description: '',
        objective: '',
        steps: '',
        example_prompt: '',
        hint: '',
        expected_outcome: '',
      });
    } catch (err) {
      console.error("Failed to create lesson", err);
    }
  };

  const handleUpdate = async () => {
    if (!editLesson) return;
    await authAxios.put(`/api/lessons/admin/edit/${editLesson.lesson_id}`, {
        title: editLesson.title,
        description: editLesson.description,
        objective: editLesson.objective,
        steps: editLesson.steps,
        example_prompt: editLesson.example_prompt,
        hint: editLesson.hint,
        expected_outcome: editLesson.expected_outcome,
      });
    setEditLesson(null);
    fetchLessons();
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Lesson Admin</h1>

      <div className="mb-6">
        <h2 className="text-lg font-semibold">Create New Lesson</h2>
        <input
            value={newLesson.title}
            onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
            placeholder="Title"
            className="border p-2 w-full mb-2"
            />

            <textarea
            value={newLesson.description}
            onChange={(e) => setNewLesson({ ...newLesson, description: e.target.value })}
            placeholder="Description"
            className="border p-2 w-full mb-2"
            />

            <input
            value={newLesson.objective || ""}
            onChange={(e) => setNewLesson({ ...newLesson, objective: e.target.value })}
            placeholder="Objective"
            className="border p-2 w-full mb-2"
            />

            <textarea
            value={newLesson.steps || ""}
            onChange={(e) => setNewLesson({ ...newLesson, steps: e.target.value })}
            placeholder="Steps (bullet list or markdown)"
            className="border p-2 w-full mb-2"
            />

            <input
            value={newLesson.example_prompt || ""}
            onChange={(e) => setNewLesson({ ...newLesson, example_prompt: e.target.value })}
            placeholder="Example Prompt"
            className="border p-2 w-full mb-2"
            />

            <input
            value={newLesson.hint || ""}
            onChange={(e) => setNewLesson({ ...newLesson, hint: e.target.value })}
            placeholder="Hint"
            className="border p-2 w-full mb-2"
            />

            <input
            value={newLesson.expected_outcome || ""}
            onChange={(e) => setNewLesson({ ...newLesson, expected_outcome: e.target.value })}
            placeholder="Expected Outcome"
            className="border p-2 w-full mb-2"
            />
        <button
          onClick={handleCreate}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Create Lesson
        </button>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-2">Existing Lessons</h2>
        {lessons.map((lesson) => (
          <div key={lesson.lesson_id} className="bg-white shadow rounded p-4 mb-4">
            {editLesson?.lesson_id === lesson.lesson_id ? (
              <>
                <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                    value={editLesson.title || ""}
                    onChange={(e) => setEditLesson({ ...editLesson, title: e.target.value })}
                    className="border p-2 w-full"
                />
                </div>

                <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Objective</label>
                <input
                value={editLesson.objective || ""}
                onChange={(e) => setEditLesson({ ...editLesson, objective: e.target.value })}
                placeholder="Objective"
                className="border p-2 w-full"
                />
                </div>

                <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Steps</label>
                <textarea
                value={editLesson.steps || ""}
                onChange={(e) => setEditLesson({ ...editLesson, steps: e.target.value })}
                placeholder="Steps (can be bullet points or numbered)"
                className="border p-2 w-full"
                />
                </div>

                <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Example Prompt</label>
                <input
                value={editLesson.example_prompt || ""}
                onChange={(e) => setEditLesson({ ...editLesson, example_prompt: e.target.value })}
                placeholder="Example Prompt"
                className="border p-2 w-full"
                />
                </div>

                <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Hint</label>
                <input
                value={editLesson.hint || ""}
                onChange={(e) => setEditLesson({ ...editLesson, hint: e.target.value })}
                placeholder="Hint"
                className="border p-2 w-full"
                />
                </div>

                <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Outcome</label>
                <input
                value={editLesson.expected_outcome || ""}
                onChange={(e) => setEditLesson({ ...editLesson, expected_outcome: e.target.value })}
                placeholder="Expected Outcome"
                className="border p-2 w-full"
                />
                </div>

                <button
                  onClick={handleUpdate}
                  className="bg-blue-600 text-white px-3 py-1 rounded mr-2"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditLesson(null)}
                  className="bg-gray-400 text-white px-3 py-1 rounded"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <h3 className="font-bold text-gray-800">{lesson.title}</h3>
                <p className="text-sm text-gray-600">{lesson.description}</p>
                <button
                  onClick={() => setEditLesson(lesson)}
                  className="mt-2 text-blue-600 hover:underline"
                >
                  Edit
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
